/**
 * DoD Pipeline Page
 * AIDLC Administrative Backend
 *
 * Visual Definition of Done pipeline showing items flowing through
 * four stages: Code Complete, Bill Code Review, Asha QA Review, Done.
 * Displays pipeline stats and a DoD rules reminder panel.
 * Consumes the backend /api/backlog endpoints.
 *
 * Personas: Ops, Orchestrator, Gatekeeper
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Badge } from '../shared/Badge';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import type {
  BacklogItem,
  DodStatus,
  DodReview,
} from '../../services/orchestrationService';
import { getBacklogItems } from '../../services/orchestrationService';

// ── Helpers ──────────────────────────────────────────────────────────

/** Priority badge config */
const priorityConfig: Record<string, { label: string; bg: string; text: string }> = {
  must: { label: 'Must', bg: 'bg-red-50', text: 'text-red-700' },
  should: { label: 'Should', bg: 'bg-orange-50', text: 'text-orange-700' },
  could: { label: 'Could', bg: 'bg-blue-50', text: 'text-blue-700' },
  wont: { label: "Won't", bg: 'bg-gray-100', text: 'text-gray-500' },
};

/** Size badge config */
const sizeConfig: Record<string, { bg: string; text: string }> = {
  S: { bg: 'bg-green-50', text: 'text-green-700' },
  M: { bg: 'bg-blue-50', text: 'text-blue-700' },
  L: { bg: 'bg-orange-50', text: 'text-orange-700' },
  XL: { bg: 'bg-red-50', text: 'text-red-700' },
};

/** Pipeline column definitions */
interface PipelineColumn {
  key: DodStatus;
  label: string;
  sublabel: string;
  headerColor: string;
  borderColor: string;
}

const PIPELINE_COLUMNS: PipelineColumn[] = [
  {
    key: 'code_complete',
    label: 'Code Complete',
    sublabel: 'Ready for review',
    headerColor: 'bg-blue-500',
    borderColor: 'border-t-blue-500',
  },
  {
    key: 'bill_review',
    label: 'Bill: Code Review',
    sublabel: 'Reviewing code quality',
    headerColor: 'bg-amber-500',
    borderColor: 'border-t-amber-500',
  },
  {
    key: 'asha_qa',
    label: 'Asha: QA Review',
    sublabel: 'Testing full user flows',
    headerColor: 'bg-purple-500',
    borderColor: 'border-t-purple-500',
  },
  {
    key: 'done',
    label: 'Done',
    sublabel: 'Bill +2 and Asha PASS',
    headerColor: 'bg-green-500',
    borderColor: 'border-t-green-500',
  },
];

/** Get Bill's rating from reviews */
function getBillRating(reviews?: DodReview[]): number | null {
  if (!reviews) return null;
  const billReview = reviews.find(
    (r) => r.reviewer.toLowerCase() === 'bill' && r.rating != null,
  );
  return billReview?.rating ?? null;
}

/** Get Asha's QA status from reviews */
function getAshaStatus(reviews?: DodReview[]): string | null {
  if (!reviews) return null;
  const ashaReview = reviews.find(
    (r) => r.reviewer.toLowerCase() === 'asha' && r.status != null,
  );
  return ashaReview?.status ?? null;
}

/** Map Bill rating to badge variant */
function ratingBadgeVariant(rating: number): 'success' | 'warning' | 'danger' | 'neutral' {
  if (rating >= 2) return 'success';
  if (rating >= 1) return 'warning';
  if (rating === 0) return 'neutral';
  return 'danger';
}

/** Categorize a backlog item into pipeline column */
function getItemColumn(item: BacklogItem): DodStatus {
  if (item.dodStatus && PIPELINE_COLUMNS.some((c) => c.key === item.dodStatus)) {
    return item.dodStatus;
  }
  // Fallback: infer from status string
  if (item.status === 'done' || item.status === 'completed') return 'done';
  if (item.status === 'review' || item.status === 'in_review') {
    const billRating = getBillRating(item.reviews);
    if (billRating !== null && billRating >= 2) return 'asha_qa';
    return 'bill_review';
  }
  if (item.status === 'code_complete') return 'code_complete';
  // Default: don't show items that aren't in the pipeline
  return item.dodStatus || 'code_complete';
}

// ── Main Component ───────────────────────────────────────────────────

export function DodPipelinePage() {
  const [items, setItems] = useState<BacklogItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRules, setShowRules] = useState(false);

  const loadItems = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getBacklogItems();
      setItems(data);
    } catch (err) {
      console.error('[DodPipeline] Failed to load:', err);
      setError('Failed to load pipeline data. Please check the backend connection.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Categorize items into pipeline columns
  const columnItems = useMemo(() => {
    const result: Record<DodStatus, BacklogItem[]> = {
      code_complete: [],
      bill_review: [],
      asha_qa: [],
      done: [],
      backlog: [],
      in_progress: [],
    };
    for (const item of items) {
      const col = getItemColumn(item);
      if (result[col]) {
        result[col].push(item);
      }
    }
    return result;
  }, [items]);

  // Pipeline stats
  const pipelineItems = [
    ...columnItems.code_complete,
    ...columnItems.bill_review,
    ...columnItems.asha_qa,
  ];
  const totalInPipeline = pipelineItems.length;
  const awaitingReview = columnItems.code_complete.length + columnItems.bill_review.length;
  const awaitingQA = columnItems.asha_qa.length;
  const completedToday = columnItems.done.filter((item) => {
    const lastReview = item.reviews?.slice(-1)[0];
    if (!lastReview?.reviewedAt) return false;
    const reviewDate = new Date(lastReview.reviewedAt).toDateString();
    return reviewDate === new Date().toDateString();
  }).length;

  if (isLoading) {
    return <LoadingSpinner message="Loading DoD pipeline..." />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-red-500 mb-4">
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-gray-700 font-medium mb-2">Unable to Load Pipeline</p>
        <p className="text-sm text-gray-500 mb-4 max-w-md text-center">{error}</p>
        <button
          onClick={loadItems}
          className="px-4 py-2 bg-ldaja-blue text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-ldaja-black">DoD Pipeline</h1>
          <p className="text-sm text-gray-500 mt-1">
            Definition of Done pipeline: Code Complete, Bill Review, Asha QA, Done.
          </p>
        </div>
        <button
          onClick={loadItems}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Pipeline Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <PipelineStatCard
          label="In Pipeline"
          value={totalInPipeline}
          colorClass="text-ldaja-blue"
          bgClass="bg-blue-50"
        />
        <PipelineStatCard
          label="Awaiting Review"
          value={awaitingReview}
          colorClass="text-amber-600"
          bgClass="bg-amber-50"
        />
        <PipelineStatCard
          label="Awaiting QA"
          value={awaitingQA}
          colorClass="text-purple-600"
          bgClass="bg-purple-50"
        />
        <PipelineStatCard
          label="Completed Today"
          value={completedToday}
          colorClass="text-green-600"
          bgClass="bg-green-50"
        />
      </div>

      {/* Pipeline Kanban Columns */}
      {items.length === 0 ? (
        <EmptyState
          title="No items in pipeline"
          description="No backlog items are currently in the DoD pipeline. Items appear here once they reach Code Complete status."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          {PIPELINE_COLUMNS.map((col) => (
            <PipelineColumnView
              key={col.key}
              column={col}
              items={columnItems[col.key] || []}
            />
          ))}
        </div>
      )}

      {/* DoD Rules Reminder (Collapsible) */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <button
          onClick={() => setShowRules(!showRules)}
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-ldaja-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-ldaja-black">DoD Rules Reminder</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Mandatory pipeline steps and commit trailer format
              </p>
            </div>
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${showRules ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {showRules && (
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="mt-3 space-y-4">
              {/* Steps */}
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  The 4 Steps (must execute sequentially)
                </h4>
                <div className="space-y-2">
                  {[
                    { step: 1, title: 'Code Complete', desc: 'Code written, compiling, no type errors in changed files.' },
                    { step: 2, title: 'Bill Code Review (FIRST)', desc: 'Bill reviews all changed files. Must receive +2 before proceeding.' },
                    { step: 3, title: 'Asha QA Review (AFTER Bill +2)', desc: 'Asha tests full user flows. Must receive PASS before proceeding.' },
                    { step: 4, title: 'Commit and Push', desc: 'Only after Bill +2 AND Asha PASS. Include review trailers.' },
                  ].map(({ step, title, desc }) => (
                    <div key={step} className="flex items-start gap-3 p-2 bg-gray-50 rounded-md">
                      <span className="shrink-0 w-6 h-6 rounded-full bg-ldaja-blue text-white text-xs font-bold flex items-center justify-center">
                        {step}
                      </span>
                      <div>
                        <p className="text-xs font-semibold text-gray-700">{title}</p>
                        <p className="text-xs text-gray-500">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Commit Trailer Format */}
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  Mandatory Commit Trailers
                </h4>
                <div className="bg-gray-900 rounded-md p-3 text-xs font-mono text-gray-300 leading-relaxed">
                  <div className="text-green-400">&lt;Short summary of what changed&gt;</div>
                  <div className="text-gray-600 mt-1">&lt;Optional longer description&gt;</div>
                  <div className="mt-2">
                    <span className="text-yellow-300">Reviewed-by:</span> Bill Torres (+2)
                  </div>
                  <div>
                    <span className="text-yellow-300">QA-verified-by:</span> Asha Patel (PASS)
                  </div>
                  <div>
                    <span className="text-yellow-300">Co-Authored-By:</span> Claude Opus 4.6 &lt;noreply@anthropic.com&gt;
                  </div>
                </div>
              </div>

              {/* Key Rule */}
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-xs font-semibold text-red-700">
                  No exceptions. Every change goes through the full pipeline.
                </p>
                <p className="text-xs text-red-600 mt-1">
                  Bill reviews FIRST, then Asha. Never skip. Never reorder.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Pipeline Stat Card ───────────────────────────────────────────────

function PipelineStatCard({
  label,
  value,
  colorClass,
  bgClass,
}: {
  label: string;
  value: number;
  colorClass: string;
  bgClass: string;
}) {
  return (
    <div className={`${bgClass} rounded-lg p-4 text-center`}>
      <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{label}</p>
      <p className={`text-2xl font-bold ${colorClass} mt-1`}>{value}</p>
    </div>
  );
}

// ── Pipeline Column View ─────────────────────────────────────────────

function PipelineColumnView({
  column,
  items,
}: {
  column: PipelineColumn;
  items: BacklogItem[];
}) {
  return (
    <div className={`bg-gray-50 rounded-lg border border-gray-200 border-t-4 ${column.borderColor} overflow-hidden`}>
      {/* Column Header */}
      <div className="px-3 py-3 bg-white border-b border-gray-100">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold text-ldaja-black">{column.label}</h3>
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
            {items.length}
          </span>
        </div>
        <p className="text-[10px] text-gray-400 mt-0.5">{column.sublabel}</p>
      </div>

      {/* Items */}
      <div className="p-2 space-y-2 min-h-[120px]">
        {items.length === 0 ? (
          <div className="flex items-center justify-center h-[100px]">
            <p className="text-[10px] text-gray-400">No items</p>
          </div>
        ) : (
          items.map((item) => (
            <PipelineItemCard key={item.id} item={item} columnKey={column.key} />
          ))
        )}
      </div>
    </div>
  );
}

// ── Pipeline Item Card ───────────────────────────────────────────────

function PipelineItemCard({
  item,
  columnKey,
}: {
  item: BacklogItem;
  columnKey: DodStatus;
}) {
  const priority = priorityConfig[item.priority] || priorityConfig.could;
  const size = sizeConfig[item.sizeCategory] || sizeConfig.M;
  const billRating = getBillRating(item.reviews);
  const ashaStatus = getAshaStatus(item.reviews);

  return (
    <div className="bg-white rounded-md border border-gray-200 p-2.5 hover:shadow-sm transition-shadow">
      {/* Title */}
      <p className="text-xs font-medium text-gray-800 mb-1.5 line-clamp-2">{item.title}</p>

      {/* Agent + badges row */}
      <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${priority.bg} ${priority.text}`}>
          {priority.label}
        </span>
        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${size.bg} ${size.text}`}>
          {item.sizeCategory}
        </span>
        {item.assignedAgent && (
          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">
            {item.assignedAgent}
          </span>
        )}
      </div>

      {/* Review status (contextual to column) */}
      {columnKey === 'bill_review' && billRating !== null && (
        <div className="mt-1.5 pt-1.5 border-t border-gray-100">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-gray-400">Bill:</span>
            <Badge variant={ratingBadgeVariant(billRating)}>
              {billRating >= 0 ? '+' : ''}{billRating}
            </Badge>
          </div>
        </div>
      )}

      {columnKey === 'asha_qa' && (
        <div className="mt-1.5 pt-1.5 border-t border-gray-100 space-y-1">
          {billRating !== null && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-gray-400">Bill:</span>
              <Badge variant={ratingBadgeVariant(billRating)}>
                {billRating >= 0 ? '+' : ''}{billRating}
              </Badge>
            </div>
          )}
          {ashaStatus && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-gray-400">Asha:</span>
              <Badge variant={ashaStatus === 'pass' ? 'success' : 'warning'}>
                {ashaStatus === 'pass' ? 'PASS' : 'Needs Fixes'}
              </Badge>
            </div>
          )}
        </div>
      )}

      {columnKey === 'done' && (
        <div className="mt-1.5 pt-1.5 border-t border-gray-100">
          <div className="flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-[10px] text-green-600 font-medium">Bill +2, Asha PASS</span>
          </div>
        </div>
      )}
    </div>
  );
}
